import React, { useState, useRef, useMemo } from 'react';
import {
  Button,
  CompanyLogoUpload,
  Icon,
  TasamaTextLogoWithTagline,
  FloatingMenu,
  type FloatingMenuItem,
  LanguageSwitcher,
  cn,
  usePageLoaderStore,
} from '@tasama/ui';
import { useCompanyLogoUrl } from '../features/my-account/hooks/useMyAccountApi';
import { useTranslation } from '@tasama/i18n';
import { useNavigate } from 'react-router-dom';
import { FeatureFlags, PORTAL_ROUTES, isEnabled } from '@tasama/config';
import { isSafeUrl } from '@tasama/utils';
import { useAuth } from '../hooks/use-auth';
// import { useUserProfile } from '../stores/useUserProfileStore';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useUnreadNotificationsCount,
} from '../features/Notifications/hooks';
import { NotificationFloatingMenuPortal } from './NotificationFloatingMenuPortal';
import { createPortal } from 'react-dom';
import type { BackendNotificationData } from '../features/Notifications/types/notification-types';
import { useUserContext } from '../stores/user-context.store';

interface AppHeaderProps {
  /** Test ID for QA automation */
  testID?: string;
  /** Additional CSS classes */
  className?: string;
  /** User name to display */
  _userName?: string;
  /** User avatar URL */
  userAvatar?: string;
  /** Notification count */
  notificationCount?: number;
  /** Callback when notification icon is clicked */
  onNotificationClick?: () => void;
  /** Callback when user profile is clicked */
  onProfileClick?: () => void;
  /** Callback when hamburger menu is clicked (mobile) */
  onMenuToggle?: () => void;
}

/**
 * AppHeader Component
 * Based on Figma design: https://www.figma.com/design/IcSV9qhrjLJh6Y3Q8F7lcr/Tasama-UI-UX-Design---Phase-1?node-id=8498-342828&m=dev
 *
 * Features:
 * - Search bar with icon
 * - Notification bell with badge
 * - User profile section
 * - Responsive design
 * - Uses design tokens for consistent styling
 */
export const AppHeader: React.FC<AppHeaderProps> = ({
  testID = 'app-header',
  className,
  onNotificationClick,
  onProfileClick,
  onMenuToggle,
}) => {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data: logoUrl, isLoading: isLogoLoading } = useCompanyLogoUrl();
  // const userProfile = useUserProfile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);

  // Fetch notifications (limit to 5 for the floating menu)
  // Only fetch when notification menu is open to avoid unnecessary API calls
  const { data: notificationsData, isLoading: isNotificationsLoading } =
    useNotifications(5, 0, {
      enabled: isNotificationMenuOpen,
    });

  // Initialize mutation hook for marking notification as read
  const markNotificationAsRead = useMarkNotificationAsRead();

  // Get user context to access unread notification count (initial value)
  const userContext = useUserContext();
  const initialUnreadCount =
    userContext?.notifications?.modules?.notifications ?? 0;

  // Fetch the real unread count on mount/reload so the bell badge reflects
  // reality immediately, instead of relying on another page (e.g. "View
  // All" -> NotificationsPage) to happen to populate the shared query cache.
  const { data: unreadCountData, refetch: refetchUnreadCount } =
    useUnreadNotificationsCount({ enabled: true });

  const unreadCount = unreadCountData?.unreadCount ?? initialUnreadCount;
  // Handle logout action
  const handleLogout = async () => {
    try {
      await logout();
      usePageLoaderStore.getState().hide();
      navigate(PORTAL_ROUTES.LOGIN);
    } catch {
      // ignore
    }
  };

  // Menu items for user profile dropdown
  const menuItems: FloatingMenuItem[] = [
    {
      id: 'my-account',
      label: t('header.myAccount'),
      icon: 'User',
      onClick: () => {
        navigate(PORTAL_ROUTES.MY_ACCOUNT);
      },
    },
    {
      id: 'logout',
      label: t('header.logout'),
      icon: 'LogOut',
      onClick: handleLogout,
    },
  ];

  // Flatten all notifications from all groups for the floating view
  const allNotifications = useMemo(() => {
    if (!notificationsData) return [];

    if (!Array.isArray(notificationsData)) return [];

    return notificationsData.flatMap(
      (group: { notifications: BackendNotificationData[] }) =>
        group.notifications || []
    );
  }, [notificationsData]);

  // Check if there are any unread notifications from user context
  const hasUnreadNotifications = unreadCount > 0;

  // Handle notification item click
  const handleNotificationItemClick = async (notification: any) => {
    try {
      const isUnread = notification.status?.toLowerCase() !== 'read';

      if (isUnread) {
        markNotificationAsRead
          .mutateAsync({
            notificationId: notification.notificationId,
          })
          .then(() => {
            refetchUnreadCount();
          })
          .catch(() => {});
      }

      const url =
        notification?.actions[0]?.url ??
        `/notifications?highlightId=${notification.notificationId}`;
      if (isSafeUrl(url)) {
        navigate(url);
      }

      setIsNotificationMenuOpen(false);
    } catch {
      // ignore
    }
  };

  // Handle view all notifications click
  const handleViewAllNotifications = () => {
    navigate(PORTAL_ROUTES.NOTIFICATIONS);
    setIsNotificationMenuOpen(false);
  };

  // Toggle menu open/close
  const handleProfileButtonClick = () => {
    setIsNotificationMenuOpen(false);
    setIsMenuOpen(!isMenuOpen);
    onProfileClick?.();
  };

  // Handle notification button click
  // On mobile (< lg), navigate directly to the notifications page
  // On desktop, toggle the floating notification panel
  const handleNotificationButtonClick = () => {
    const isDesktop = window.matchMedia('(min-width: 900px)').matches;
    if (!isDesktop) {
      navigate(PORTAL_ROUTES.NOTIFICATIONS);
      return;
    }
    setIsMenuOpen(false);
    setIsNotificationMenuOpen(!isNotificationMenuOpen);
    onNotificationClick?.();
  };

  const handleMoveToHome = () => {
    navigate(PORTAL_ROUTES.HOME);
  };

  return (
    <header
      className={cn(
        'flex items-center justify-between',
        'w-full h-[76px]',
        'px-4 lg:px-6',
        'bg-white',
        'border-b border-gray-200',
        'sticky top-0 z-sticky',
        className
      )}
      data-testid={testID}
    >
      {/* Logo Section */}
      <div className="shrink-0">
        <button
          className="flex items-center cursor-pointer"
          onClick={handleMoveToHome}
          aria-label={t('header.navigateHome')}
          data-testid={`${testID}-home-button`}
        >
          <TasamaTextLogoWithTagline
            width={166}
            height={35}
            className="w-[166px] h-[35px]"
            testID={`${testID}-logo`}
          />
        </button>
      </div>

      {/* TDF-1391: while acting for a client, every page must show whose account
          this is, with a one-step way to change it. A client user never sees it. */}
      {userContext?.actingCompany && (
        <div
          className="hidden lg:flex items-center gap-3"
          data-testid={`${testID}-acting-context`}
        >
          <span className="text-sm font-medium text-fg-primary whitespace-nowrap">
            {t('superAdmin.selectCompany.switchLabel')}
          </span>
          <button
            type="button"
            onClick={() => navigate(PORTAL_ROUTES.SELECT_COMPANY)}
            aria-label={t('superAdmin.selectCompany.switchAction')}
            title={t('superAdmin.selectCompany.switchAction')}
            className="flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2 cursor-pointer max-w-[318px]"
            data-testid={`${testID}-acting-company-switch`}
          >
            <span className="text-sm text-fg-primary truncate">
              {userContext.actingCompany.name}
            </span>
            <Icon name="Pencil" size={16} className="text-fg-brand-primary" />
          </button>
        </div>
      )}

      {/* Right Section - Notifications & Profile */}
      <div className="flex items-center gap-3 lg:gap-4">
        {/* Language Switcher */}
        {isEnabled(FeatureFlags.LANGUAGE_SWITCHER) && (
          <LanguageSwitcher testID={`${testID}-language-switcher`} />
        )}

        {/* Notification Bell */}
        <Button
          ref={notificationButtonRef}
          type="button"
          className={cn(
            'relative',
            'flex items-center justify-center',
            'w-10 h-10',
            'rounded-full',
            'bg-gray-100',
            'text-gray-600',
            'cursor-pointer',
            'hover:bg-gray-200',
            'transition-colors duration-200'
          )}
          onClick={handleNotificationButtonClick}
          data-testid={`${testID}-notification-button`}
          aria-label={t('header.notifications')}
          aria-expanded={isNotificationMenuOpen}
          aria-haspopup="menu"
        >
          <Icon
            name="Bell"
            size={20}
            className="text-brand-blue-500"
            testID={`${testID}-notification-icon`}
          />
          {/* Unread Indicator Dot */}
          {hasUnreadNotifications && (
            <div
              className={cn(
                'absolute top-[11px] end-[11px]',
                'w-[8px] h-[8px]',
                'bg-brand-blue-500',
                'rounded-full'
              )}
              data-testid={`${testID}-notification-unread-dot`}
              aria-label={t('notifications.unreadNotificationsAriaLabel')}
            />
          )}
        </Button>

        {/* Notification Floating Menu */}
        {isNotificationMenuOpen &&
          notificationButtonRef.current &&
          createPortal(
            <NotificationFloatingMenuPortal
              notifications={allNotifications}
              anchorRef={notificationButtonRef}
              onClose={() => setIsNotificationMenuOpen(false)}
              onNotificationClick={handleNotificationItemClick}
              onViewAllClick={handleViewAllNotifications}
              testID={`${testID}-notification-menu`}
              isLoading={isNotificationsLoading}
            />,
            document.body
          )}

        {/* User Profile */}
        <Button
          ref={profileButtonRef}
          type="button"
          className={cn(
            'flex items-center justify-center',
            'w-10 h-10',
            'cursor-pointer',
            'rounded-full',
            'hover:bg-gray-100',
            'transition-colors duration-200',
            'focus:outline-none'
          )}
          onClick={handleProfileButtonClick}
          data-testid={`${testID}-profile-button`}
          aria-label={t('header.userProfile')}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
        >
          {/* User Avatar */}
          <div
            className={cn(
              'flex items-center justify-center',
              'w-10 h-10',
              'rounded-full',
              'bg-gray-100',
              'text-gray-600'
            )}
            data-testid={`${testID}-avatar`}
          >
            <Icon name="User" size={20} className="text-brand-blue-500" />
          </div>
        </Button>

        {/* Floating Menu */}
        <FloatingMenu
          items={menuItems}
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          anchorRef={profileButtonRef}
          placement="bottom-end"
          size="md"
          testID={`${testID}-profile-menu`}
        />

        {/* Hamburger Menu - Mobile only */}
        <Button
          type="button"
          className={cn(
            'flex lg:hidden items-center justify-center',
            'w-10 h-10',
            'rounded-full',
            'bg-gray-100',
            'text-gray-600',
            'cursor-pointer',
            'hover:bg-gray-200',
            'transition-colors duration-200'
          )}
          onClick={onMenuToggle}
          data-testid={`${testID}-menu-button`}
          aria-label={t('navigation.openMenu')}
        >
          <Icon name="Menu" size={20} className="text-brand-blue-500" />
        </Button>

        {/* Company Logo - Desktop only, hidden when no logo available */}
        {!isLogoLoading && logoUrl && (
          <div className="hidden lg:block">
            <CompanyLogoUpload
              logoSrc={logoUrl}
              logoAlt="Company Logo"
              readOnly
              size="sm"
              testID={`${testID}-company-logo`}
            />
          </div>
        )}
      </div>
    </header>
  );
};

AppHeader.displayName = 'AppHeader';

export type { AppHeaderProps };
